/**
 * Buttons for Dropdowns
 *
 * @module
 */

export class SelectButtons {
    /**
     * Control a select dropdown with a group of buttons.
     *
     * @param {HTMLElement} select
     * @param {HTMLElement} buttonsParent
     * @param {string} [datasetAttribute]
     */
    constructor(select, buttonsParent, datasetAttribute = 'value') {
        this.select = select;
        this.buttonsParent = buttonsParent;
        this.datasetAttribute = datasetAttribute;
    }

    /**
     * Change which button is displayed as selected.
     *
     * @private
     * @param {HTMLElement} button
     * @return {void}
     */
    markAsSelected(button) {
        this.buttonsParent.querySelector('.selected').classList.remove('selected');
        button.classList.add('selected');
    }

    /**
     * Get the value represented by the given button.
     *
     * @private
     * @param {HTMLElement} button
     * @return {string}
     */
    getButtonValue(button) {
        return button.dataset[this.datasetAttribute];
    }

    /**
     * Change the value selected in the dropdown.
     *
     * @private
     * @param {string} value
     * @return {void}
     */
    updateValue(value) {
        this.select.value = value;
        this.select.dispatchEvent(new Event('change'));
    }

    /**
     * Select the click button and sync the dropdown value.
     *
     * @private
     * @param {HTMLElement} button
     * @return {void}
     */
    onButtonClick(button) {
        if (button.classList.contains('selected')) {
            return;
        }
        this.markAsSelected(button);
        this.updateValue(this.getButtonValue(button));
    }

    /**
     * Initialize button click handler.
     *
     * @private
     * @return {void}
     */
    listenForButtonClicks() {
        this.buttonsParent.addEventListener('click', (e) => {
            if (e.target.nodeName === 'BUTTON') {
                this.onButtonClick(e.target);
            }
        });
    }

    /**
     * Initialize button click handler.
     *
     * @private
     * @throws {TypeError} If the given select element is not valid.
     * @throws {Error} If the buttons to replace the options are not found.
     * @return {void}
     */
    assertValidElements() {
        if (!(this.select instanceof HTMLElement) || this.select.nodeName !== 'SELECT') {
            throw new TypeError('Not a select element.');
        }
        if (
            !(this.buttonsParent instanceof HTMLElement) ||
            this.buttonsParent.querySelectorAll('button').length === 0
        ) {
            throw new Error('Could not find any buttons.');
        }
    }

    /**
     * Validate the given elements and init the button click handler.
     *
     * @return {void}
     */
    init() {
        this.assertValidElements();
        this.listenForButtonClicks();
    }
}
